import {
  collection, doc, getDoc, getDocs, query, where,
  onSnapshot, orderBy,
} from 'firebase/firestore'
import { db } from './firebase'
import { authHeaders } from './auth'
import { SessionError } from './session'
import type {
  Organization, OrgMember, SessionMetadata, Artifact, Message,
  Participant, Reflection,
} from '../types'
import { FUNCTIONS_BASE_URL } from './functionsBase'

/**
 * Teams are created and joined server-side: the invite code is a secret that admits
 * the bearer to every transcript the team has produced, so it is verified where a
 * client cannot see or forge it.
 */
async function orgCall<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new SessionError(
      detail.message || detail.error || 'Request failed',
      response.status,
      detail.error || 'REQUEST_FAILED',
    )
  }
  return response.json()
}

export async function createOrganization(
  name: string,
  _creatorUid: string,
  displayName: string,
): Promise<{ orgId: string; inviteToken: string }> {
  return orgCall('createOrgFn', { name, displayName })
}

export async function resolveInvite(token: string): Promise<{ orgId: string; name: string } | null> {
  try {
    return await orgCall('resolveInviteFn', { token })
  } catch {
    return null
  }
}

export async function joinOrganization(
  token: string,
  displayName: string,
): Promise<{ orgId: string } | null> {
  try {
    return await orgCall('joinOrgFn', { token, displayName })
  } catch (err) {
    // An anonymous visitor following an invite link needs to be told to sign in,
    // not shown "invalid invite code". Everything else really is a bad invite.
    if (err instanceof SessionError && err.code === 'SIGN_IN_REQUIRED') throw err
    return null
  }
}

export async function getUserOrganizations(uid: string): Promise<Organization[]> {
  const q = query(collection(db, 'organizations'), where('memberUids', 'array-contains', uid))
  const snap = await getDocs(q)
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Organization))
}

export async function getOrganization(orgId: string): Promise<Organization | null> {
  const snap = await getDoc(doc(db, 'organizations', orgId))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as Organization
}


export function subscribeToOrgMembers(
  orgId: string,
  callback: (members: (OrgMember & { uid: string })[]) => void,
) {
  return onSnapshot(
    collection(db, 'organizations', orgId, 'members'),
    (snap) => {
      const members = snap.docs.map(d => ({ uid: d.id, ...d.data() } as OrgMember & { uid: string }))
      callback(members)
    },
  )
}

export type SessionWithId = SessionMetadata & { id: string }

export async function getOrgSessions(orgId: string): Promise<SessionWithId[]> {
  const q = query(
    collection(db, 'sessions'),
    where('organizationId', '==', orgId),
    orderBy('createdAt', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SessionWithId))
}

export async function getSessionArtifacts(sessionId: string): Promise<Artifact[]> {
  const snap = await getDocs(collection(db, 'sessions', sessionId, 'artifacts'))
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Artifact))
}

export async function getSessionMessages(sessionId: string): Promise<Message[]> {
  const q = query(
    collection(db, 'sessions', sessionId, 'messages'),
    orderBy('timestamp', 'asc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Message))
}

export async function getSessionParticipants(sessionId: string): Promise<Participant[]> {
  const snap = await getDocs(collection(db, 'sessions', sessionId, 'participants'))
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant))
}

export async function getSessionReflections(sessionId: string): Promise<Reflection[]> {
  const snap = await getDocs(collection(db, 'sessions', sessionId, 'reflections'))
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Reflection))
}
